/**
 * Multi Select Component.
 *
 * A multi-value combobox that shows selected items as removable pill
 * badges. The dropdown stays open after each selection for fast
 * multi-pick. Supports typeahead filtering, keyboard navigation,
 * and optional creatable mode for adding new values on the fly.
 *
 * The dropdown renders via a portal with position: fixed so it
 * escapes parent stacking contexts (ActionDrawers, cards, etc.).
 */

import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, Check, Plus, X } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  creatable?: boolean;
  disabled?: boolean;
  className?: string;
}

let multiSelectCounter = 0;

function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  label,
  creatable = false,
  disabled = false,
  className,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [instanceId] = useState(() => `multi-select-${++multiSelectCounter}`);
  const filterRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase().trim();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  const queryHasExactMatch = useMemo(() => {
    if (!query.trim()) return true;
    const q = query.toLowerCase().trim();
    return options.some(
      (o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q,
    );
  }, [options, query]);

  useEffect(() => setHighlightIdx(0), [filtered.length]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 300;
    const openAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow;
    setDropdownPos({
      top: openAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen, value.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        const dropdown = document.getElementById(instanceId);
        if (dropdown && dropdown.contains(e.target as Node)) return;
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, instanceId]);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => {
      setIsOpen(false);
      setQuery("");
    };
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [isOpen]);

  const toggleValue = useCallback(
    (optValue: string) => {
      if (selectedSet.has(optValue)) {
        onChange(value.filter((v) => v !== optValue));
      } else {
        onChange([...value, optValue]);
      }
    },
    [value, onChange, selectedSet],
  );

  const removeValue = useCallback(
    (optValue: string) => {
      onChange(value.filter((v) => v !== optValue));
    },
    [value, onChange],
  );

  const handleCreate = useCallback(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return;
    if (!selectedSet.has(trimmed)) {
      onChange([...value, trimmed]);
    }
    setQuery("");
  }, [query, value, onChange, selectedSet]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen) {
        const highlighted = filtered[highlightIdx];
        if (highlighted) {
          toggleValue(highlighted.value);
        } else if (creatable && query.trim() && !queryHasExactMatch) {
          handleCreate();
        }
      } else {
        setIsOpen(true);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
    } else if (e.key === "Backspace" && !query && value.length > 0) {
      removeValue(value[value.length - 1]!);
    } else if (e.key === "Tab") {
      setIsOpen(false);
      setQuery("");
    }
  };

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => filterRef.current?.focus(), 0);
  };

  const getLabelForValue = useCallback(
    (v: string) => {
      const opt = options.find((o) => o.value === v);
      return opt ? opt.label : v;
    },
    [options],
  );

  const dropdown = isOpen
    ? createPortal(
        <div
          id={instanceId}
          className="fixed z-[9999] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)] animate-scale-in"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Filter input */}
          <div className="relative border-b border-[var(--color-border)]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-tertiary)] pointer-events-none" />
            <input
              ref={filterRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type to filter..."
              className="w-full h-[36px] pl-9 pr-3 bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none"
            />
          </div>

          {/* Options list */}
          <div className="max-h-56 overflow-y-auto">
            {filtered.map((option, idx) => {
              const isSelected = selectedSet.has(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() => toggleValue(option.value)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-[var(--color-border)]/30 last:border-0 transition-colors ${
                    idx === highlightIdx
                      ? "bg-[var(--color-interactive-primary)]/[0.06]"
                      : "hover:bg-[var(--surface-sunken)]"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center h-4 w-4 rounded border shrink-0 transition-colors ${
                      isSelected
                        ? "bg-[var(--color-interactive-primary)] border-[var(--color-interactive-primary)]"
                        : "border-[var(--border-default)]"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span className="text-sm text-[var(--text-primary)] truncate flex-1">
                    {option.label}
                  </span>
                </div>
              );
            })}

            {filtered.length === 0 && !creatable && (
              <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
                {query ? `No results for "${query}"` : "No options available"}
              </div>
            )}

            {filtered.length === 0 && creatable && query.trim() && (
              <div className="px-3 py-3 text-center text-xs text-[var(--text-tertiary)]">
                No matches found — press Enter or click below to add it
              </div>
            )}
          </div>

          {/* Create option */}
          {creatable && query.trim() && !queryHasExactMatch && (
            <div className="border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-[var(--color-interactive-primary)] hover:bg-[var(--color-interactive-primary)]/[0.06] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add &ldquo;{query.trim()}&rdquo;
              </button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        onClick={openDropdown}
        className={`min-h-[38px] flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] cursor-pointer transition-all duration-200 hover:border-[var(--border-strong)] ${
          isOpen
            ? "shadow-[var(--focus-ring)] border-[var(--border-focus)]"
            : ""
        } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        {/* Selected pills */}
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-[var(--radius-sm)] bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)] text-xs font-medium"
          >
            {getLabelForValue(v)}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeValue(v);
              }}
              className="p-0.5 rounded hover:bg-[var(--color-interactive-primary)]/20 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        {/* Placeholder when empty */}
        {value.length === 0 && (
          <span className="text-sm text-[var(--text-tertiary)] py-0.5">
            {placeholder}
          </span>
        )}

        {/* Chevron */}
        <ChevronDown
          className={`h-3.5 w-3.5 text-[var(--text-tertiary)] ml-auto shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {dropdown}
    </div>
  );
}

export default MultiSelect;
